// src/components/home/PostComposer.jsx
import React, {
  useState, useRef, useEffect, useMemo, useCallback,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../../firebase';
import {
  collection, addDoc, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import {
  validateMedia,
  uploadMedia,
  isVideoFile,
  POST_BG_PRESETS,
  FONT_FAMILIES,
  FONT_SIZES,
  FONT_WEIGHTS,
  TEXT_ALIGNS,
  DEFAULT_FONT,
  getFontClass,
} from '../../utils/feedHelpers';
import { sanitizeHtml, htmlToPlainText } from '../../utils/sanitizeHtml';
import { useRichEditor } from '../../hooks/useRichEditor';
import RichTextToolbar from './RichTextToolbar';

/* ================================================================== */
/* Visibility options — Option B (Public / Private / Only Me)          */
/* ================================================================== */

const VISIBILITY_OPTIONS = [
  {
    value: 'public',
    label: 'Public',
    icon: 'fa-globe',
    hint: 'Anyone can see this',
    activeCls: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 font-semibold',
  },
  {
    value: 'private',
    label: 'Private',
    icon: 'fa-users',
    hint: 'Only logged-in members',
    activeCls: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-semibold',
  },
  {
    value: 'onlyMe',
    label: 'Only Me',
    icon: 'fa-lock',
    hint: 'Only you and admins',
    activeCls: 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-100 font-semibold',
  },
];

const getVisibilityMeta = (value) =>
  VISIBILITY_OPTIONS.find((o) => o.value === value) || VISIBILITY_OPTIONS[0];

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const toLocalDateTimeValue = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

/* ================================================================== */
/* Avatar — reusable                                                     */
/* ================================================================== */

const ComposerAvatar = ({ photo, name, size = 'md' }) => {
  const dim = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10';
  return (
    <div
      className={`${dim} rounded-full bg-gradient-to-br from-orange to-orange-dark text-white flex items-center justify-center font-bold flex-shrink-0 overflow-hidden`}
    >
      {photo ? (
        <img src={photo} alt="" className="w-full h-full object-cover" />
      ) : (
        name?.[0]?.toUpperCase() || '?'
      )}
    </div>
  );
};

/* ================================================================== */
/* Trigger — inline bar at the top of the feed                         */
/* ================================================================== */

export const PostComposerTrigger = ({ onOpen }) => {
  const { user, userData } = useAuth();
  const displayName = userData?.displayName || user?.displayName || 'User';
  const photo = userData?.photoUrl;

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Open post composer"
      className="w-full bg-white dark:bg-gray-800 rounded-xl shadow p-3 mb-4 flex items-center gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-750 transition group"
    >
      <ComposerAvatar photo={photo} name={displayName} />
      <span className="flex-1 text-sm text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition">
        What's on your mind, {displayName.split(' ')[0]}?
      </span>
      <span className="flex items-center gap-3 text-orange">
        <i className="fas fa-photo-video text-lg" aria-hidden="true" />
        <i className="fas fa-palette text-lg" aria-hidden="true" />
      </span>
    </button>
  );
};

/* ================================================================== */
/* Floating trigger — appears when the inline one scrolls out of view  */
/* ================================================================== */

export const FloatingPostTrigger = ({ onOpen, visible }) => {
  const { user, userData } = useAuth();
  const displayName = userData?.displayName || user?.displayName || 'User';
  const photo = userData?.photoUrl;

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          onClick={onOpen}
          initial={{ opacity: 0, y: -14, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -14, scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 360, damping: 30 }}
          aria-label="Open post composer"
          className="fixed top-20 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-1.5rem)] max-w-2xl bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-full shadow-lg border border-gray-200/60 dark:border-gray-700/60 pl-2 pr-2 py-1.5 flex items-center gap-2.5 text-left hover:shadow-xl transition-shadow"
        >
          <ComposerAvatar photo={photo} name={displayName} size="sm" />
          <span className="flex-1 text-sm text-gray-500 dark:text-gray-400 truncate">
            What's on your mind, {displayName.split(' ')[0]}?
          </span>
          <span className="flex items-center gap-2 text-orange flex-shrink-0">
            <i className="fas fa-photo-video text-base" aria-hidden="true" />
            <span className="bg-orange text-white rounded-full w-7 h-7 flex items-center justify-center">
              <i className="fas fa-pen text-[11px]" aria-hidden="true" />
            </span>
          </span>
        </motion.button>
      )}
    </AnimatePresence>
  );
};

/* ================================================================== */
/* Modal shell — backdrop click & Esc disabled                         */
/* ================================================================== */

const PostComposerModal = ({ open, onClose, children }) => {
  const panelRef = useRef(null);

  // Body scroll lock — restore the actual previous value
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Focus the panel on open
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => panelRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center overflow-y-auto p-3 md:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Create a post"
          // NOTE: backdrop clicks do nothing — close only via X button
        >
          <motion.div
            ref={panelRef}
            tabIndex={-1}
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl my-6 md:my-0 focus:outline-none"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                <i className="fas fa-pen-to-square text-orange" aria-hidden="true" />
                Create Post
              </h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-300 flex items-center justify-center transition"
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ================================================================== */
/* Discard-confirm dialog                                              */
/* ================================================================== */

const DiscardConfirmDialog = ({ open, onCancel, onConfirm }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
      if (e.key === 'Enter') onConfirm?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel, onConfirm]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="discard-title"
          aria-describedby="discard-desc"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ type: 'spring', stiffness: 340, damping: 26 }}
            className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-5"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <i
                  className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"
                  aria-hidden="true"
                />
              </div>
              <div className="flex-1">
                <h3
                  id="discard-title"
                  className="text-sm font-bold text-gray-900 dark:text-white mb-1"
                >
                  Discard this post?
                </h3>
                <p
                  id="discard-desc"
                  className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed"
                >
                  You have unsaved content. If you close now, your text and
                  media will be lost.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                Keep editing
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition"
              >
                Discard
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ================================================================== */
/* PostComposerForm                                                    */
/* ================================================================== */

export const PostComposerForm = ({ onPosted, onClose, registerDirtyCheck }) => {
  const { user, userData, isAdmin } = useAuth();

  /* -------- Form state -------- */
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [coverIndex, setCoverIndex] = useState(0);
  const [visibility, setVisibility] = useState('public');

  /* -------- Design state -------- */
  const [bgPreset, setBgPreset] = useState('default');
  const [font, setFont] = useState(DEFAULT_FONT);
  const [designTab, setDesignTab] = useState('bg');
  const [showDesignPanel, setShowDesignPanel] = useState(false);

  /* -------- Preview state -------- */
  const [showPreview, setShowPreview] = useState(false);

  /* -------- Admin date state -------- */
  const [postDate, setPostDate] = useState('');
  const [showDatePanel, setShowDatePanel] = useState(false);

  /* -------- Submit state -------- */
  const [posting, setPosting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');

  /* -------- Link dialog -------- */
  const [linkDialog, setLinkDialog] = useState(null); // { url, hasSelection }

  /* -------- Rich editor -------- */
  const editor = useRichEditor();
  const [plainText, setPlainText] = useState('');

  const handleEditorInput = useCallback(() => {
    const html = editor.editorRef.current?.innerHTML || '';
    setPlainText(htmlToPlainText(html));
  }, [editor.editorRef]);

  const previewUrlsRef = useRef([]);

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  /* ================================================================ */
  /* Expose a dirty-check to the parent                               */
  /* ================================================================ */

  useEffect(() => {
    if (typeof registerDirtyCheck !== 'function') return undefined;
    registerDirtyCheck(() => {
      const html = editor.editorRef.current?.innerHTML || '';
      const hasText = htmlToPlainText(html).trim().length > 0;
      const hasMedia = files.length > 0;
      return hasText || hasMedia;
    });
    return () => registerDirtyCheck(null);
  }, [registerDirtyCheck, files.length, editor.editorRef]);

  /* ================================================================ */
  /* Files — no cap                                                   */
  /* ================================================================ */

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;

    const errors = [];
    const accepted = [];
    for (const f of picked) {
      const err = validateMedia(f);
      if (err) errors.push(err);
      else accepted.push(f);
    }
    setError(errors.length ? errors.join('\n') : '');

    if (!accepted.length) return;

    const combined = [...files, ...accepted];

    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    const urls = combined.map((f) => ({
      url: URL.createObjectURL(f),
      type: isVideoFile(f) ? 'video' : 'image',
    }));
    previewUrlsRef.current = urls.map((u) => u.url);

    setFiles(combined);
    setPreviews(urls);
  };

  const removeAt = (idx) => {
    URL.revokeObjectURL(previews[idx].url);
    previewUrlsRef.current = previewUrlsRef.current.filter((_, i) => i !== idx);
    const newFiles = files.filter((_, i) => i !== idx);
    const newPreviews = previews.filter((_, i) => i !== idx);
    setFiles(newFiles);
    setPreviews(newPreviews);
    if (idx === coverIndex) setCoverIndex(0);
    else if (idx < coverIndex) setCoverIndex((c) => Math.max(0, c - 1));
  };

  const setCover = (idx) => setCoverIndex(idx);

  /* ================================================================ */
  /* Reset                                                             */
  /* ================================================================ */

  const reset = () => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
    if (editor.editorRef.current) editor.editorRef.current.innerHTML = '';
    setPlainText('');
    setFiles([]);
    setPreviews([]);
    setCoverIndex(0);
    setVisibility('public');
    setBgPreset('default');
    setFont(DEFAULT_FONT);
    setPostDate('');
    setError('');
    setProgress({ done: 0, total: 0 });
    setShowDesignPanel(false);
    setShowDatePanel(false);
    setShowPreview(false);
    setDesignTab('bg');
  };

  /* ================================================================ */
  /* Submit                                                            */
  /* ================================================================ */

  const handleSubmit = async () => {
    const rawHtml = editor.editorRef.current?.innerHTML || '';
    const cleanHtml = sanitizeHtml(rawHtml);
    const plain = htmlToPlainText(cleanHtml).trim();

    if (!plain && files.length === 0) {
      setError('Add text, photo or video.');
      return;
    }
    setError('');
    setPosting(true);
    setProgress({ done: 0, total: files.length });

    try {
      const media = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const url = await uploadMedia(file, 'posts');
        media.push({
          url,
          type: isVideoFile(file) ? 'video' : 'image',
          isCover: i === coverIndex,
        });
        setProgress({ done: i + 1, total: files.length });
      }

      if (media.length > 0 && !media.some((m) => m.isCover)) {
        media[0].isCover = true;
      }

      const approved = isAdmin === true;

      const payload = {
        authorId: user.uid,
        authorName: userData?.displayName || user.displayName || 'User',
        authorPhoto: userData?.photoUrl || null,
        content: plain,
        contentHtml: cleanHtml,
        media,
        photos: media.filter((m) => m.type === 'image').map((m) => m.url),
        visibility,                        // ✅ 'public' | 'private' | 'onlyMe'
        design: {
          bgPreset: bgPreset === 'default' ? null : bgPreset,
          font: bgPreset === 'default' ? null : font,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        likeCount: 0,
        commentCount: 0,
        shareCount: 0,
        likedBy: [],
        approved,
        approvalStatus: approved ? 'approved' : 'pending',
        approvedBy: null,
        approvedAt: null,
      };

      if (isAdmin && postDate) {
        const d = new Date(postDate);
        if (!isNaN(d.getTime())) {
          payload.postDate = Timestamp.fromDate(d);
          payload.backdated = true;
        }
      }
      if (!payload.postDate) {
        payload.postDate = serverTimestamp();
      }

      await addDoc(collection(db, 'posts'), payload);

      reset();
      onPosted?.();
      onClose?.();
    } catch (err) {
      console.error('Post failed:', err);
      setError(err.message || 'Failed to publish post.');
    } finally {
      setPosting(false);
    }
  };

  /* ================================================================== */
  /* Derived                                                             */
  /* ================================================================== */

  const displayName = userData?.displayName || user.displayName || 'User';
  const photo = userData?.photoUrl;
  const activeBg = POST_BG_PRESETS.find((p) => p.id === bgPreset);
  const hasBg = bgPreset !== 'default';
  const fontClass = getFontClass(font);

  const fontAlignBox =
    font?.align === 'left'
      ? 'justify-start text-left'
      : font?.align === 'right'
      ? 'justify-end text-right'
      : 'justify-center text-center';

  const quickDates = useMemo(
    () => [
      { label: 'Now', value: '' },
      {
        label: '1h ago',
        value: toLocalDateTimeValue(new Date(Date.now() - 60 * 60 * 1000)),
      },
      {
        label: 'Yesterday',
        value: toLocalDateTimeValue(new Date(Date.now() - 24 * 60 * 60 * 1000)),
      },
      {
        label: '1w ago',
        value: toLocalDateTimeValue(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
      },
      {
        label: '1m ago',
        value: toLocalDateTimeValue(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
      },
    ],
    [],
  );

  const canPost = !posting && (plainText.trim() || files.length > 0);

  const visibilityMeta = getVisibilityMeta(visibility);

  /* ================================================================== */
  /* Link dialog                                                         */
  /* ================================================================== */

  const requestLink = () => {
    const sel = window.getSelection();
    const hasSelection =
      !!sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed;
    setLinkDialog({ url: '', hasSelection });
  };

  const confirmLink = () => {
    if (!linkDialog) return;
    const { url } = linkDialog;
    if (!url.trim()) {
      editor.exec('unlink');
    } else {
      editor.insertLink(url);
    }
    setLinkDialog(null);
  };

  /* ================================================================== */
  /* Preview mode                                                        */
  /* ================================================================== */

  if (showPreview) {
    const rawHtml = editor.editorRef.current?.innerHTML || '';
    const previewHtml = sanitizeHtml(rawHtml);

    // ✅ Preview badge — three-tier aware
    const previewBadge = (() => {
      const vis = visibility;
      if (vis === 'public') {
        return {
          cls: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
          icon: 'fa-globe',
          label: 'Public',
        };
      }
      if (vis === 'private') {
        return {
          cls: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
          icon: 'fa-users',
          label: 'Members',
        };
      }
      return {
        cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
        icon: 'fa-lock',
        label: 'Only Me',
      };
    })();

    return (
      <div>
        <div className="flex items-center justify-between mb-3 pb-3 border-b dark:border-gray-700">
          <div className="flex items-center gap-2">
            <i className="fas fa-eye text-orange" aria-hidden="true" />
            <h3 className="text-sm font-bold text-gray-800 dark:text-white">
              Preview
            </h3>
          </div>
          <button
            type="button"
            onClick={() => setShowPreview(false)}
            className="text-xs text-orange hover:underline font-medium flex items-center gap-1"
          >
            <i className="fas fa-pen" aria-hidden="true" />
            Continue editing
          </button>
        </div>

        <div className="border dark:border-gray-700 rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 p-4">
            <ComposerAvatar photo={photo} name={displayName} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                  {displayName}
                </p>
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${previewBadge.cls}`}
                >
                  <i
                    className={`fas ${previewBadge.icon} text-[8px]`}
                    aria-hidden="true"
                  />
                  {previewBadge.label}
                </span>
                {!isAdmin && (
                  <span className="text-[10px] text-yellow-600 dark:text-yellow-400 font-medium flex items-center gap-1">
                    <i className="fas fa-clock text-[8px]" aria-hidden="true" />
                    Pending review
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">just now</p>
            </div>
          </div>

          {previewHtml && htmlToPlainText(previewHtml).trim() && (
            <div className={`px-4 pb-3 ${hasBg ? 'pt-1' : ''}`}>
              {hasBg ? (
                <div
                  className={`rounded-2xl px-5 py-6 min-h-[120px] flex ${fontAlignBox} ${activeBg.bg} ${activeBg.text}`}
                >
                  <div
                    className={`${fontClass} whitespace-pre-wrap break-words w-full post-rich-content`}
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                </div>
              ) : (
                <div
                  className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words post-rich-content"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              )}
            </div>
          )}

          {previews.length > 0 && (
            <div className="relative bg-black">
              {(() => {
                const ordered =
                  previews.length > 1
                    ? [
                        previews[coverIndex],
                        ...previews.filter((_, i) => i !== coverIndex),
                      ]
                    : previews;
                const first = ordered[0];
                return first.type === 'video' ? (
                  <video
                    src={first.url}
                    controls
                    playsInline
                    className="w-full max-h-[600px] object-contain"
                  />
                ) : (
                  <img
                    src={first.url}
                    alt=""
                    className="w-full max-h-[600px] object-contain"
                  />
                );
              })()}
              {previews.length > 1 && (
                <>
                  <div className="absolute top-3 left-3 bg-orange text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-lg">
                    <i className="fas fa-star text-[9px]" aria-hidden="true" />
                    Cover
                  </div>
                  <div className="absolute top-3 right-3 bg-black/60 text-white text-xs rounded-full px-2 py-1">
                    1/{previews.length}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="px-2 py-1 border-t dark:border-gray-700 flex">
            {isAdmin ? (
              <>
                <button
                  disabled
                  className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-gray-400 cursor-default"
                >
                  <i className="far fa-heart text-lg" aria-hidden="true" />
                  Like
                </button>
                <button
                  disabled
                  className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-gray-400 cursor-default"
                >
                  <i className="far fa-comment text-lg" aria-hidden="true" />
                  Comment
                </button>
                <button
                  disabled
                  className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-gray-400 cursor-default"
                >
                  <i className="fas fa-share text-lg" aria-hidden="true" />
                  Share
                </button>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center py-2 text-xs text-yellow-600 dark:text-yellow-400">
                <i className="fas fa-info-circle mr-1" aria-hidden="true" />
                Will be visible after admin approval
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex justify-between items-center gap-2">
          <button
            type="button"
            onClick={reset}
            disabled={posting}
            className="text-sm text-gray-500 hover:text-red-500 flex items-center gap-1.5 disabled:opacity-50"
          >
            <i className="fas fa-trash-alt" aria-hidden="true" />
            Discard
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowPreview(false)}
              disabled={posting}
              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md text-sm disabled:opacity-50"
            >
              <i className="fas fa-pen mr-1" aria-hidden="true" />
              Edit
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={posting}
              className="px-5 py-2 bg-orange hover:bg-orange-dark text-white rounded-md text-sm font-medium disabled:opacity-50"
            >
              {posting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-1" aria-hidden="true" />
                  Posting…
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane mr-1" aria-hidden="true" />
                  {isAdmin ? 'Post Now' : 'Submit for Review'}
                </>
              )}
            </button>
          </div>
        </div>

        {posting && progress.total > 0 && (
          <div className="mt-3">
            <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-orange transition-all duration-300"
                style={{ width: `${(progress.done / progress.total) * 100}%` }}
              />
            </div>
            <p className="text-xs text-center text-gray-500 mt-1">
              Uploading {progress.done} of {progress.total}…
            </p>
          </div>
        )}

        {error && (
          <div className="mt-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 text-xs rounded-md p-2 whitespace-pre-line">
            {error}
          </div>
        )}
      </div>
    );
  }

  /* ================================================================== */
  /* Edit mode                                                           */
  /* ================================================================== */

  return (
    <div>
      <div className="flex gap-3 mb-3">
        <ComposerAvatar photo={photo} name={displayName} />

        <div
          className={`flex-1 rounded-2xl overflow-hidden focus-within:ring-2 focus-within:ring-orange transition-colors ${
            hasBg
              ? `${activeBg.bg} ${activeBg.text} ${fontClass}`
              : 'bg-gray-100 dark:bg-gray-700 dark:text-white'
          }`}
        >
          <RichTextToolbar
            editor={editor}
            onRequestLink={requestLink}
            disabled={posting}
          />

          <div
            ref={editor.editorRef}
            contentEditable={!posting}
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="Post content"
            data-placeholder={`What's on your mind, ${displayName.split(' ')[0]}?`}
            onInput={handleEditorInput}
            onBlur={editor.saveSelection}
            onKeyUp={editor.saveSelection}
            onMouseUp={editor.saveSelection}
            className={`post-rich-editor min-h-[120px] max-h-[400px] overflow-y-auto p-3 text-sm leading-relaxed focus:outline-none ${
              hasBg ? 'text-inherit' : 'text-gray-900 dark:text-white'
            }`}
            style={{ wordBreak: 'break-word' }}
          />
        </div>
      </div>

      {/* ============================================================ */}
      {/* Visibility toggle — Option B (3 tiers)                       */}
      {/* ============================================================ */}
      <div
        className="flex items-center gap-2 mb-3 flex-wrap"
        style={{ paddingLeft: '52px' }}
      >
        {VISIBILITY_OPTIONS.map((opt) => {
          const active = visibility === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setVisibility(opt.value)}
              disabled={posting}
              aria-pressed={active}
              title={opt.hint}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full transition disabled:opacity-50 ${
                active
                  ? opt.activeCls
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <i className={`fas ${opt.icon}`} aria-hidden="true" />
              {opt.label}
            </button>
          );
        })}
        <span className="text-[10px] text-gray-400 ml-1">
          {visibilityMeta.hint}
        </span>
      </div>

      {/* Design panel */}
      {showDesignPanel && (
        <div className="mb-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 border dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="flex gap-1 bg-white dark:bg-gray-800 rounded-lg p-1 border dark:border-gray-700">
              <button
                type="button"
                onClick={() => setDesignTab('bg')}
                className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-md transition ${
                  designTab === 'bg'
                    ? 'bg-orange text-white font-medium'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <i className="fas fa-palette text-[10px]" aria-hidden="true" />
                Background
              </button>
              <button
                type="button"
                onClick={() => setDesignTab('font')}
                className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-md transition ${
                  designTab === 'font'
                    ? 'bg-orange text-white font-medium'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <i className="fas fa-font text-[10px]" aria-hidden="true" />
                Font
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowDesignPanel(false)}
              className="text-xs text-gray-500 hover:text-orange"
            >
              Close
            </button>
          </div>

          {designTab === 'bg' && (
            <>
              <div className="flex flex-wrap gap-2">
                {POST_BG_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setBgPreset(p.id)}
                    aria-label={p.label}
                    title={p.label}
                    className={`w-9 h-9 rounded-lg border-2 transition-all ${
                      bgPreset === p.id
                        ? 'border-orange ring-2 ring-orange/30 scale-105'
                        : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                    } ${p.bg || 'bg-white dark:bg-gray-800'}`}
                  >
                    {p.id === 'default' && (
                      <i
                        className="fas fa-ban text-gray-400 text-xs"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                ))}
              </div>
              {bgPreset === 'default' && (
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2">
                  Pick a background to unlock font styling.
                </p>
              )}
            </>
          )}

          {designTab === 'font' && (
            <>
              {!hasBg ? (
                <div className="text-center py-4 text-xs text-gray-500">
                  <i
                    className="fas fa-info-circle mb-1 block text-lg opacity-40"
                    aria-hidden="true"
                  />
                  Pick a background first to enable font styling.
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1.5">
                      Font family
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {FONT_FAMILIES.map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => setFont({ ...font, family: f.id })}
                          className={`${f.className} text-xs px-2.5 py-1 rounded-md border transition ${
                            font.family === f.id
                              ? 'bg-orange text-white border-orange'
                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-orange'
                          }`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1.5">
                      Size
                    </p>
                    <div className="flex gap-1.5">
                      {FONT_SIZES.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setFont({ ...font, size: s.id })}
                          className={`text-xs px-3 py-1 rounded-md border transition ${
                            font.size === s.id
                              ? 'bg-orange text-white border-orange'
                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-orange'
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1.5">
                      Weight
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {FONT_WEIGHTS.map((w) => (
                        <button
                          key={w.id}
                          type="button"
                          onClick={() => setFont({ ...font, weight: w.id })}
                          className={`${w.className} text-xs px-2.5 py-1 rounded-md border transition ${
                            font.weight === w.id
                              ? 'bg-orange text-white border-orange'
                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-orange'
                          }`}
                        >
                          {w.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1.5">
                        Style
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          setFont({ ...font, italic: !font.italic })
                        }
                        className={`italic text-xs px-3 py-1 rounded-md border transition w-full ${
                          font.italic
                            ? 'bg-orange text-white border-orange'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-orange'
                        }`}
                      >
                        Italic
                      </button>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1.5">
                        Align
                      </p>
                      <div className="flex gap-1">
                        {TEXT_ALIGNS.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => setFont({ ...font, align: a.id })}
                            aria-label={a.label}
                            title={a.label}
                            className={`flex-1 text-xs py-1 rounded-md border transition ${
                              font.align === a.id
                                ? 'bg-orange text-white border-orange'
                                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-orange'
                            }`}
                          >
                            <i
                              className={`fas ${a.icon} text-[11px]`}
                              aria-hidden="true"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setFont(DEFAULT_FONT)}
                    className="text-[11px] text-gray-500 hover:text-orange underline"
                  >
                    Reset font to default
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Admin date panel */}
      {isAdmin && showDatePanel && (
        <div className="mb-3 p-3 rounded-xl bg-orange/5 border border-orange/30">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-orange flex items-center gap-1.5">
              <i className="fas fa-clock" aria-hidden="true" />
              Post date (admin)
            </p>
            <button
              type="button"
              onClick={() => setShowDatePanel(false)}
              className="text-xs text-gray-500 hover:text-orange"
            >
              Close
            </button>
          </div>

          <input
            type="datetime-local"
            value={postDate}
            onChange={(e) => setPostDate(e.target.value)}
            className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange"
          />

          <div className="flex flex-wrap gap-1.5 mt-2">
            {quickDates.map((q) => (
              <button
                key={q.label}
                type="button"
                onClick={() => setPostDate(q.value)}
                className={`text-[11px] px-2 py-1 rounded-full transition ${
                  postDate === q.value
                    ? 'bg-orange text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-orange/10'
                }`}
              >
                {q.label}
              </button>
            ))}
          </div>

          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2">
            Leave empty to use current date. Backdated posts appear in the
            correct chronological position.
          </p>
        </div>
      )}

      {/* Media previews */}
      {previews.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 flex items-center justify-between">
            <span>Media ({previews.length})</span>
            {previews.length > 1 && (
              <span className="text-[10px] text-orange">
                Click image to set as cover
              </span>
            )}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {previews.map((p, i) => {
              const isCover = i === coverIndex;
              return (
                <div
                  key={`${p.url}-${i}`}
                  className={`relative group rounded-lg overflow-hidden ${
                    isCover && previews.length > 1 ? 'ring-2 ring-orange' : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setCover(i)}
                    disabled={posting}
                    className="block w-full"
                    title="Click to set as cover"
                  >
                    {p.type === 'video' ? (
                      <video
                        src={p.url}
                        className="w-full h-24 object-cover"
                        muted
                        playsInline
                      />
                    ) : (
                      <img
                        src={p.url}
                        alt=""
                        className="w-full h-24 object-cover"
                      />
                    )}
                  </button>
                  {p.type === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center">
                        <i
                          className="fas fa-play text-white text-xs"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  )}
                  {isCover && previews.length > 1 && (
                    <span className="absolute top-1 left-1 bg-orange text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1">
                      <i className="fas fa-star text-[8px]" aria-hidden="true" />
                      Cover
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    disabled={posting}
                    aria-label="Remove"
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition disabled:opacity-50"
                  >
                    <i className="fas fa-times text-xs" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 text-xs rounded-md p-2 mb-3 whitespace-pre-line">
          {error}
        </div>
      )}

      {posting && progress.total > 0 && (
        <div className="mb-3">
          <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-orange transition-all duration-300"
              style={{ width: `${(progress.done / progress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Action row */}
      <div className="flex items-center justify-between pt-2 border-t dark:border-gray-700 flex-wrap gap-2">
        <div className="flex items-center gap-1">
          <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
            <i
              className="fas fa-photo-video text-green-500"
              aria-hidden="true"
            />
            <span className="hidden sm:inline">Photo / Video</span>
            <input
              type="file"
              accept="image/*,video/mp4,video/webm,video/quicktime"
              multiple
              onChange={handleFiles}
              disabled={posting}
              className="hidden"
            />
          </label>

          <button
            type="button"
            onClick={() => setShowDesignPanel((s) => !s)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition ${
              showDesignPanel || hasBg
                ? 'bg-orange/10 text-orange'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
            title="Design & font"
          >
            <i className="fas fa-palette text-purple-500" aria-hidden="true" />
            <span className="hidden sm:inline">Design</span>
          </button>

          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowDatePanel((s) => !s)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition ${
                showDatePanel || postDate
                  ? 'bg-orange/10 text-orange'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
              title="Set custom post date"
            >
              <i className="fas fa-clock" aria-hidden="true" />
              <span className="hidden sm:inline">
                {postDate ? 'Date set' : 'Date'}
              </span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            disabled={!canPost}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium border border-orange text-orange hover:bg-orange/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
            title="Preview before posting"
          >
            <i className="fas fa-eye" aria-hidden="true" />
            <span className="hidden sm:inline">Preview</span>
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canPost}
            className="bg-orange hover:bg-orange-dark text-white px-5 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {posting ? 'Posting…' : isAdmin ? 'Post' : 'Submit'}
          </button>
        </div>
      </div>

      {!isAdmin && (
        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1">
          <i className="fas fa-info-circle" aria-hidden="true" />
          Your post will be visible after admin approval.
        </p>
      )}

      {/* Link dialog */}
      {linkDialog && (
        <div
          className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setLinkDialog(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl p-4 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="font-semibold mb-2 text-sm text-gray-900 dark:text-white">
              {linkDialog.hasSelection ? 'Add link' : 'Insert link'}
            </h4>
            <input
              type="url"
              value={linkDialog.url}
              onChange={(e) =>
                setLinkDialog({ ...linkDialog, url: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirmLink();
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  setLinkDialog(null);
                }
              }}
              placeholder="https://example.com"
              autoFocus
              className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => setLinkDialog(null)}
                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmLink}
                className="px-3 py-1.5 text-sm bg-orange text-white rounded hover:bg-orange-dark"
              >
                {linkDialog.url ? 'Add' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ================================================================== */
/* Default — trigger + floating pill + modal + discard confirm         */
/* ================================================================== */

const PostComposer = ({ onPosted, suppressFloat = false }) => {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showFloating, setShowFloating] = useState(false);
  const dirtyCheckRef = useRef(null);
  const inlineTriggerRef = useRef(null);

  /* ----- Register the "is dirty?" callback from the form ----- */
  const registerDirtyCheck = useCallback((fn) => {
    dirtyCheckRef.current = fn;
  }, []);

  /* ----- X button clicked ----- */
  const handleRequestClose = useCallback(() => {
    const isDirty = dirtyCheckRef.current ? dirtyCheckRef.current() : false;
    if (isDirty) {
      setConfirmOpen(true);
    } else {
      setOpen(false);
    }
  }, []);

  /* ----- Confirmed discard ----- */
  const handleConfirmDiscard = useCallback(() => {
    setConfirmOpen(false);
    setOpen(false);
  }, []);

  /* ----- Cancelled discard ----- */
  const handleCancelDiscard = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  /* ---------------------------------------------------------------- */
  /* Show floating trigger when the inline one leaves the viewport    */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const node = inlineTriggerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show float only when the inline trigger has scrolled ABOVE the viewport
        setShowFloating(
          !entry.isIntersecting && entry.boundingClientRect.top < 0,
        );
      },
      { threshold: 0, rootMargin: '0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Never show the floating pill while a dialog is open or suppressed
  const floatingVisible =
    showFloating && !open && !confirmOpen && !suppressFloat;

  return (
    <>
      <div ref={inlineTriggerRef}>
        <PostComposerTrigger onOpen={() => setOpen(true)} />
      </div>

      <FloatingPostTrigger
        onOpen={() => setOpen(true)}
        visible={floatingVisible}
      />

      <PostComposerModal open={open} onClose={handleRequestClose}>
        <PostComposerForm
          onPosted={onPosted}
          onClose={() => setOpen(false)}
          registerDirtyCheck={registerDirtyCheck}
        />
      </PostComposerModal>

      <DiscardConfirmDialog
        open={confirmOpen}
        onCancel={handleCancelDiscard}
        onConfirm={handleConfirmDiscard}
      />
    </>
  );
};

export default PostComposer;